import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, Navigate } from 'react-router-dom';
import { Icons } from '../components/Icons';
import FileViewer from '../components/FileViewer';
import DocumentCard from '../components/DocumentCard';
import { CardsGridSkeleton } from '../components/SkeletonLoader';
import {
  getAllRequiredDocuments,
  addRequiredDocument,
  deleteRequiredDocument,
  getCurrentAcademicYear,
  getAllAcademicYears,
  createAcademicYear,
  endAcademicYear,
  downloadAllByType,
  updateAcademicYearLockDate,
  updateReminderSchedule,
  updateGracePeriod,
  getCurrentOffcampusYear,
  getAllOffcampusYears,
  createOffcampusAcademicYear,
  updateOffcampusAcademicYear,
  endOffcampusAcademicYear,
  downloadAllOffcampus,
  BASE_URL,
  getAdminStaff,
  inviteAdminStaff,
  updateAdminStaff,
  deleteAdminStaff,
} from '../services/api';

// --- Settings Page ---
const Settings = () => {
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop();

  const adminType = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.admin_type || null;
    } catch {
      return null;
    }
  })();
  const isOffCampus = adminType === 'offcampus';

  const [lockDate, setLockDate] = useState('2027-06-02');
  const [lockTime, setLockTime] = useState('17:00');
  const [originalLockDate, setOriginalLockDate] = useState('');
  const [originalLockTime, setOriginalLockTime] = useState('');
  const [gracePeriod, setGracePeriod] = useState(0);
  const [reminderSchedule, setReminderSchedule] = useState('7 days, 3 days, 1 days');
  const [currentAcYearId, setCurrentAcYearId] = useState(null);
  const [deadlineSaving, setDeadlineSaving] = useState(false);
  const [deadlineError, setDeadlineError] = useState('');
  const [deadlineSaved, setDeadlineSaved] = useState(false);

  // Real requirements data from the backend instead of hardcoded mock lists
  const [accreditationReqs, setAccreditationReqs] = useState([]);
  const [reaccreditationReqs, setReaccreditationReqs] = useState([]);
  const [offCampusReqs, setOffCampusReqs] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);

  const [statusUpdates, setStatusUpdates] = useState(true);
  const [deadlineReminders, setDeadlineReminders] = useState(true);

  // Staff Management states
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ full_name: '', slsu_email: '', admin_type: 'accreditation' });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [staffActionLoading, setStaffActionLoading] = useState(null);
  const [deletingStaff, setDeletingStaff] = useState(null);
  const [deletingStaffLoading, setDeletingStaffLoading] = useState(false);

  const fetchStaff = async () => {
    try {
      setStaffLoading(true);
      const res = await getAdminStaff();
      setStaffList(res.data);
    } catch (e) {
      console.error('Failed to fetch staff:', e);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleInvite = async () => {
    setInviteError('');
    setInviteSuccess('');
    if (!inviteForm.full_name.trim() || !inviteForm.slsu_email.trim()) {
      setInviteError('Full name and email are required.');
      return;
    }
    if (!inviteForm.slsu_email.endsWith('@slsu.edu.ph')) {
      setInviteError('Only @slsu.edu.ph addresses are allowed.');
      return;
    }
    setInviteLoading(true);
    try {
      await inviteAdminStaff(inviteForm);
      setInviteSuccess(`Invite sent to ${inviteForm.slsu_email}. They will receive an email to set their password.`);
      setInviteForm({ full_name: '', slsu_email: '', admin_type: 'accreditation' });
      await fetchStaff();
    } catch (e) {
      setInviteError(e?.response?.data?.detail || 'Failed to send invite.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleToggleStaffStatus = async (member) => {
    const newStatus = member.status === 'active' ? 'inactive' : 'active';
    setStaffActionLoading(member.user_id);
    try {
      await updateAdminStaff(member.user_id, { status: newStatus });
      await fetchStaff();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update staff status.');
    } finally {
      setStaffActionLoading(null);
    }
  };

  const handleDeleteStaff = async () => {
    if (!deletingStaff) return;
    setDeletingStaffLoading(true);
    try {
      await deleteAdminStaff(deletingStaff.user_id);
      setDeletingStaff(null);
      await fetchStaff();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete admin account.');
    } finally {
      setDeletingStaffLoading(false);
    }
  };

  // Academic Year states
  const [academicYears, setAcademicYears] = useState([]);
  const [currentAcYear, setCurrentAcYear] = useState(null);
  const [newYearLabel, setNewYearLabel] = useState('');
  const [newYearStart, setNewYearStart] = useState('');
  const [newYearEndDate, setNewYearEndDate] = useState('');
  const [showEndCycleModal, setShowEndCycleModal] = useState(false);
  const [endCycleConfirmText, setEndCycleConfirmText] = useState('');
  const [showDownloadPromptModal, setShowDownloadPromptModal] = useState(false);
  const [downloadAllDone, setDownloadAllDone] = useState(false);
  const [downloadingAllDocs, setDownloadingAllDocs] = useState(false);
  const [acknowledgedDownload, setAcknowledgedDownload] = useState(false);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const [academicYearError, setAcademicYearError] = useState('');

  const [currentOffcampusYear, setCurrentOffcampusYear] = useState(null);
  const [offcampusYears, setOffcampusYears] = useState([]);
  const [offcampusYearLoading, setOffcampusYearLoading] = useState(false);
  const [offcampusYearLabel, setOffcampusYearLabel] = useState('');
  const [offcampusStartDate, setOffcampusStartDate] = useState('');
  const [offcampusLockDate, setOffcampusLockDate] = useState('');
  const [showOffcampusEndModal, setShowOffcampusEndModal] = useState(false);
  const [offcampusEndConfirmText, setOffcampusEndConfirmText] = useState('');
  const [offcampusDownloadDone, setOffcampusDownloadDone] = useState(false);
  const [offcampusAcknowledged, setOffcampusAcknowledged] = useState(false);
  const [showOffcampusDownloadPrompt, setShowOffcampusDownloadPrompt] = useState(false);
  const [downloadingOffcampusDocs, setDownloadingOffcampusDocs] = useState(false);
  const [offcampusYearError, setOffcampusYearError] = useState('');

  // Modal States
  const [viewingDoc, setViewingDoc] = useState(null);
  const [deletingReq, setDeletingReq] = useState(null);
  const [addingReqType, setAddingReqType] = useState(null);
  const [newReqFile, setNewReqFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedMoaAdminId, setSelectedMoaAdminId] = useState(null);

  // Fetches all required documents from the backend and splits them by category
  const fetchRequirements = async () => {
    try {
      setLoadingReqs(true);
      const res = await getAllRequiredDocuments();
      const all = res.data;
      setAccreditationReqs(all.filter(d => d.category === 'accreditation'));
      setReaccreditationReqs(all.filter(d => d.category === 'reaccreditation'));
      setOffCampusReqs(all.filter(d => d.category === 'offcampus'));
    } catch (error) {
      console.error('Failed to fetch required documents:', error);
    } finally {
      setLoadingReqs(false);
    }
  };

  // Fetches the academic year list and current cycle from the backend
  const fetchAcademicYears = async () => {
    try {
      setAcademicYearLoading(true);
      setAcademicYearError('');
      const [allRes, currentRes] = await Promise.allSettled([
        getAllAcademicYears(),
        getCurrentAcademicYear(),
      ]);
      if (allRes.status === 'fulfilled') setAcademicYears(allRes.value.data);
      if (currentRes.status === 'fulfilled') setCurrentAcYear(currentRes.value.data);
      else setCurrentAcYear(null);
    } catch (error) {
      setAcademicYearError('Failed to load academic year data.');
    } finally {
      setAcademicYearLoading(false);
    }
  };

  // Fetches the off-campus academic year list and current cycle from the backend
  const fetchOffcampusAcademicYears = async () => {
    setOffcampusYearLoading(true);
    try {
      const [currentRes, allRes] = await Promise.all([
        getCurrentOffcampusYear().catch(() => ({ data: null })),
        getAllOffcampusYears().catch(() => ({ data: [] }))
      ]);
      setCurrentOffcampusYear(currentRes.data);
      const years = Array.isArray(allRes.data) ? allRes.data : [];
      setOffcampusYears(years);
    } catch (error) {
      console.error('Failed to fetch off-campus academic years:', error);
      setOffcampusYears([]);
    } finally {
      setOffcampusYearLoading(false);
    }
  };

  // Only fetches requirements when the Requirements tab is active
  useEffect(() => {
    if (currentTab === 'requirements') {
      fetchRequirements();
    }
    if (currentTab === 'academic-year') {
      fetchAcademicYears();
      if (isOffCampus) fetchOffcampusAcademicYears();
    }
    if (currentTab === 'staff') {
      fetchStaff();
    }
    if (currentTab === 'deadline') {
      const loadDeadline = async () => {
        try {
          const res = await getCurrentAcademicYear();
          const year = res.data;
          setCurrentAcYearId(year.academic_year_id);
          // Parse the stored datetime directly as a local naive string to avoid
          // UTC ↔ local timezone shifting (e.g. new Date() would shift +08:00 offsets).
          const rawLockDate = year.lock_date;
          const [datePart, rawTimePart] = rawLockDate.split('T');
          // Strip any timezone suffix (Z, +HH:MM, -HH:MM) before splitting HH:MM
          const timePart = (rawTimePart || '00:00:00').replace(/Z|([+-]\d{2}:\d{2})$/, '');
          const [rawHours, rawMins] = timePart.split(':');
          setLockDate(datePart);
          setLockTime(`${String(rawHours).padStart(2, '0')}:${String(rawMins).padStart(2, '0')}`);
          setOriginalLockDate(datePart);
          setOriginalLockTime(`${String(rawHours).padStart(2, '0')}:${String(rawMins).padStart(2, '0')}`);
          if (year.reminder_schedule) {
            setReminderSchedule(year.reminder_schedule);
          }
          if (year.grace_period_days !== undefined) {
            setGracePeriod(year.grace_period_days || 0);
          }
        } catch (e) {
          setDeadlineError('No active academic year found. Create one in the Academic Year tab first.');
        }
      };
      loadDeadline();
    }
  }, [currentTab]);

  const getTitle = () => {
    if (currentTab === 'requirements') return 'Requirements';
    if (currentTab === 'notifications') return 'Notifications';
    if (currentTab === 'academic-year') return 'Academic Year';
    if (currentTab === 'staff') return 'Staff Management';
    return 'Deadline';
  };

  // Saves the new requirement to the backend using the dropped file
  const handleUploadNewRequirement = async () => {
    if (!newReqFile) return;
    try {
      const name = newReqFile.name.replace(/\.[^/.]+$/, '');
      const formData = new FormData();
      const categoryMap = { 'accreditation': 'accreditation', 'reaccreditation': 'reaccreditation', 'off-campus': 'offcampus' };
      formData.append('category', categoryMap[addingReqType] || 'accreditation');
      formData.append('document_name', name);
      formData.append('file', newReqFile);

      await addRequiredDocument(formData);
      await fetchRequirements();
      setAddingReqType(null);
      setNewReqFile(null);
      setIsDragOver(false);
    } catch (error) {
      console.error('Failed to add requirement:', error);
      const detail = error?.response?.data?.detail || 'Unknown error';
      alert(`Failed to add the requirement: ${detail}`);
    }
  };

  // Deletes the requirement from the backend
  const handleDeleteRequirement = async () => {
    if (!deletingReq) return;
    // Map display type to backend category string
    const categoryMap = { 'accreditation': 'accreditation', 'reaccreditation': 'reaccreditation', 'off-campus': 'offcampus' };
    const backendCategory = categoryMap[deletingReq.type] || 'accreditation';
    try {
      await deleteRequiredDocument(backendCategory, deletingReq.id);
      await fetchRequirements();
    } catch (error) {
      console.error('Failed to delete requirement:', error);
      const detail = error?.response?.data?.detail || 'Unknown error';
      alert(`Failed to delete the requirement: ${detail}`);
    } finally {
      setDeletingReq(null);
    }
  };

  // Creates a new academic year cycle on the backend
  const handleCreateAcademicYear = async () => {
    if (!newYearLabel.trim() || !newYearStart || !newYearEndDate) {
      setAcademicYearError('Please fill in all fields: Year Label, Start Date, and Lock Date.');
      return;
    }
    try {
      setAcademicYearError('');
      await createAcademicYear({
        year_label: newYearLabel,
        start_date: new Date(newYearStart).toISOString(),
        end_date: new Date(newYearEndDate).toISOString(),
      });
      setNewYearLabel('');
      setNewYearStart('');
      setNewYearEndDate('');
      await fetchAcademicYears();
    } catch (error) {
      setAcademicYearError('Failed to create academic year. Please try again.');
    }
  };

  const handleApplyDeadline = async () => {
    if (!currentAcYearId) {
      setDeadlineError('No active academic year. Create one in the Academic Year tab first.');
      return;
    }
    if (!lockDate) {
      setDeadlineError('Please select a lock date.');
      return;
    }
    setDeadlineSaving(true);
    setDeadlineError('');
    try {
      // Combines the date and time inputs into a single ISO datetime string
      const newCombined = lockTime
        ? `${lockDate}T${lockTime}:00`
        : `${lockDate}T23:59:00`;
      const origCombined = originalLockTime
        ? `${originalLockDate}T${originalLockTime}:00`
        : `${originalLockDate}T23:59:00`;

      if (newCombined !== origCombined) {
        await updateAcademicYearLockDate(currentAcYearId, newCombined);
      }
      await updateReminderSchedule(currentAcYearId, reminderSchedule);
      await updateGracePeriod(currentAcYearId, gracePeriod);
      setDeadlineSaved(true);
      setTimeout(() => setDeadlineSaved(false), 3000);
    } catch (error) {
      setDeadlineError('Failed to update deadline. Please try again.');
    } finally {
      setDeadlineSaving(false);
    }
  };

  const handleDownloadAllBeforeYearEnd = async () => {
    setDownloadingAllDocs(true);
    try {
      const response = await downloadAllByType();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'All-Documents-By-Type.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDownloadAllDone(true);
    } catch (error) {
      alert('Failed to download documents. Please try again.');
    } finally {
      setDownloadingAllDocs(false);
    }
  };

  // Ends the currently active academic year cycle
  const handleEndAcademicYear = async () => {
    if (!currentAcYear) return;
    try {
      await endAcademicYear(currentAcYear.academic_year_id);
      setShowEndCycleModal(false);
      setEndCycleConfirmText('');
      await fetchAcademicYears();
    } catch (error) {
      setAcademicYearError('Failed to end academic year. Please try again.');
    }
  };

  // Creates a new off-campus academic year cycle on the backend
  const handleCreateOffcampusYear = async () => {
    if (!offcampusYearLabel.trim() || !offcampusStartDate || !offcampusLockDate) {
      setOffcampusYearError('Please fill in all fields: Year Label, Start Date, and End Date.');
      return;
    }
    try {
      setOffcampusYearError('');
      await createOffcampusAcademicYear({
        year_label: offcampusYearLabel,
        start_date: new Date(offcampusStartDate).toISOString(),
        lock_date: new Date(offcampusLockDate).toISOString()
      });
      setOffcampusYearLabel('');
      setOffcampusStartDate('');
      setOffcampusLockDate('');
      await fetchOffcampusAcademicYears();
    } catch (error) {
      console.error('Failed to create off-campus academic year:', error);
      setOffcampusYearError('Failed to create academic year. Please try again.');
    }
  };

  // Ends the currently active off-campus academic year cycle
  const handleEndOffcampusYear = async () => {
    if (!currentOffcampusYear) return;
    try {
      await endOffcampusAcademicYear(currentOffcampusYear.offcampus_year_id);
      setShowOffcampusEndModal(false);
      setOffcampusEndConfirmText('');
      fetchOffcampusAcademicYears();
    } catch (error) {
      console.error('Failed to end off-campus academic year:', error);
    }
  };

  const handleDownloadOffcampusBeforeYearEnd = async () => {
    setDownloadingOffcampusDocs(true);
    try {
      const response = await downloadAllOffcampus();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'All-OffCampus-Documents.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setOffcampusDownloadDone(true);
    } catch (error) {
      alert('Failed to download documents.');
    } finally {
      setDownloadingOffcampusDocs(false);
    }
  };

  if (isOffCampus && currentTab === 'deadline') {
    return <Navigate to="/admin/settings/academic-year" replace />;
  }

  const isMoaReq = (name) => /1\.03f11|memorandum of agreement|\bmoa\b/i.test(name || '');

  const offCampusMoaItems = offCampusReqs.filter((r) => isMoaReq(r.document_name));
  const offCampusNonMoaItems = offCampusReqs.filter((r) => !isMoaReq(r.document_name));

  let offCampusDisplayReqs = offCampusReqs;
  if (offCampusMoaItems.length > 1) {
    const activeMoaItem =
      offCampusMoaItems.find((m) => m.required_document_id === selectedMoaAdminId) ||
      offCampusMoaItems[0];
    const unifiedMoaCard = {
      ...activeMoaItem,
      isMoaGroup: true,
      moaVariants: offCampusMoaItems,
    };
    offCampusDisplayReqs = [...offCampusNonMoaItems, unifiedMoaCard];
  }

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa] relative">
      <div className="flex-1 overflow-y-auto px-8 py-8 custom-scrollbar">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{getTitle()}</h2>

        {currentTab === 'deadline' && (
          <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden max-w-4xl">
            <div className="bg-[#086C53] px-6 py-4">
              <h3 className="text-white font-semibold text-[15px]">Manage Deadline</h3>
            </div>
            <div className="p-8 space-y-6">

              <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Lock Date</h4>
                  <p className="text-[13px] text-gray-500">The day when submission portal will be closed.</p>
                </div>
                <div className="relative w-64">
                  <input type="date" value={lockDate} onChange={(e) => setLockDate(e.target.value)} className="w-full bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 cursor-pointer" />
                </div>
              </div>

              <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Lock Time</h4>
                  <p className="text-[13px] text-gray-500">The time when submission portal will be closed.</p>
                </div>
                <div className="relative w-64">
                  <input type="time" value={lockTime} onChange={(e) => setLockTime(e.target.value)} className="w-full border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 bg-white cursor-pointer" />
                </div>
              </div>

              <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Grace Period</h4>
                  <p className="text-[13px] text-gray-500">Allow additional time after the lock date for late submissions.</p>
                </div>
                <div className="relative w-64">
                  <select
                    value={gracePeriod}
                    onChange={(e) => setGracePeriod(Number(e.target.value))}
                    className="w-64 bg-white border border-gray-300 rounded-lg
                    py-2 pl-4 pr-4 text-sm focus:outline-none
                    focus:border-[#086C53] font-semibold text-gray-700
                    appearance-none cursor-pointer"
                  >
                    <option value={0}>No Grace Period</option>
                    <option value={1}>1 Day</option>
                    <option value={2}>2 Days</option>
                    <option value={3}>3 Days</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Set Reminder Schedule</h4>
                  <p className="text-[13px] text-gray-500">Send reminder schedule when deadline is approaching.</p>
                </div>
                <div className="relative w-64">
                  <select value={reminderSchedule} onChange={(e) => setReminderSchedule(e.target.value)} className="w-full appearance-none border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 bg-white cursor-pointer">
                    <option>7 days, 3 days, 1 days</option>
                    <option>3 days, 1 days</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <Icons.ChevronDown />
                  </div>
                </div>
              </div>

              {deadlineError && (
                <p className="text-red-500 text-sm font-medium">{deadlineError}</p>
              )}
              {deadlineSaved && (
                <p className="text-green-600 text-sm font-medium">
                  Deadline updated successfully.
                </p>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button onClick={() => { setDeadlineError(''); setDeadlineSaved(false); }} className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={handleApplyDeadline} disabled={deadlineSaving} className="px-6 py-2 bg-[#086C53] rounded-lg text-sm font-semibold text-white hover:bg-[#065440] transition-colors shadow-sm">{deadlineSaving ? 'Saving...' : 'Apply'}</button>
              </div>

            </div>
          </div>
        )}

        {currentTab === 'requirements' && (
          loadingReqs ? (
            <div className="space-y-6 max-w-6xl">
              <CardsGridSkeleton count={8} />
            </div>
          ) : (
            <div className="space-y-8 max-w-6xl">
              {/* Accreditation Requirements — Accreditation Admin Only */}
              {!isOffCampus && (
                <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6 overflow-hidden">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                    <div>
                      <h3 className="text-gray-900 font-bold text-lg">Accreditation Requirements</h3>
                      <p className="text-gray-500 text-xs mt-0.5">{accreditationReqs.length} required documents for new organizations seeking accreditation</p>
                    </div>
                    <button onClick={() => setAddingReqType('accreditation')} className="bg-[#086C53] text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center space-x-2 hover:bg-[#065440] transition-colors shadow-sm cursor-pointer">
                      <Icons.Plus />
                      <span>Add New</span>
                    </button>
                  </div>
                  {accreditationReqs.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-12">No accreditation requirements posted yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {accreditationReqs.map((req, index) => (
                        <DocumentCard
                          key={req.required_document_id}
                          req={req}
                          index={index}
                          onView={(doc) => setViewingDoc(doc)}
                          onDelete={() => setDeletingReq({ id: req.required_document_id, name: req.document_name, type: 'accreditation' })}
                          isAdmin={true}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Re-accreditation Requirements — Accreditation Admin Only */}
              {!isOffCampus && (
                <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6 overflow-hidden">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                    <div>
                      <h3 className="text-gray-900 font-bold text-lg">Re-accreditation Requirements</h3>
                      <p className="text-gray-500 text-xs mt-0.5">{reaccreditationReqs.length} required documents for existing organizations</p>
                    </div>
                    <button onClick={() => setAddingReqType('reaccreditation')} className="bg-[#086C53] text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center space-x-2 hover:bg-[#065440] transition-colors shadow-sm cursor-pointer">
                      <Icons.Plus />
                      <span>Add New</span>
                    </button>
                  </div>
                  {reaccreditationReqs.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-12">No re-accreditation requirements posted yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {reaccreditationReqs.map((req, index) => (
                        <DocumentCard
                          key={req.required_document_id}
                          req={req}
                          index={index}
                          onView={(doc) => setViewingDoc(doc)}
                          onDelete={() => setDeletingReq({ id: req.required_document_id, name: req.document_name, type: 'reaccreditation' })}
                          isAdmin={true}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Off-Campus Activity Requirements — Off-Campus Admin Only */}
              {isOffCampus && (
                <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 p-6 overflow-hidden">
                  <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                    <div>
                      <h3 className="text-gray-900 font-bold text-lg">Off-Campus Activity Requirements</h3>
                      <p className="text-gray-500 text-xs mt-0.5">{offCampusDisplayReqs.length} required document categories ({offCampusReqs.length} total files)</p>
                    </div>
                    <button onClick={() => setAddingReqType('off-campus')} className="bg-[#086C53] text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center space-x-2 hover:bg-[#065440] transition-colors shadow-sm cursor-pointer">
                      <Icons.Plus />
                      <span>Add New</span>
                    </button>
                  </div>
                  {offCampusDisplayReqs.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-12">No off-campus requirements posted yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {offCampusDisplayReqs.map((req, index) => (
                        <DocumentCard
                          key={req.isMoaGroup ? `moa-group-${req.required_document_id}` : req.required_document_id}
                          req={req}
                          index={index}
                          onView={(doc) => setViewingDoc(doc)}
                          onDelete={() => setDeletingReq({ id: req.required_document_id, name: req.document_name, type: 'off-campus' })}
                          isAdmin={true}
                          onSelectMoaVariant={(selectedId) => setSelectedMoaAdminId(selectedId)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}

        {currentTab === 'notifications' && (
          <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden max-w-4xl">
            <div className="bg-[#086C53] px-6 py-4">
              <h3 className="text-white font-semibold text-[15px]">Notification Settings</h3>
              <p className="text-green-100 text-[11px] mt-0.5">Configure automated alerts</p>
            </div>
            <div className="p-8 space-y-6">

              <div className="flex justify-between items-center border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Document Status Updates</h4>
                  <p className="text-[13px] text-gray-400">Notify orgs when document status changes.</p>
                </div>
                <button
                  onClick={() => setStatusUpdates(!statusUpdates)}
                  className={`w-[46px] h-[26px] rounded-full transition-colors relative flex items-center ${statusUpdates ? 'bg-[#086C53]' : 'bg-gray-300'}`}
                >
                  <div className={`w-[20px] h-[20px] bg-white rounded-full transition-transform absolute shadow-sm ${statusUpdates ? 'translate-x-[22px]' : 'translate-x-[3px]'}`}></div>
                </button>
              </div>

              <div className="flex justify-between items-center border-b border-gray-100 pb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Deadline Reminders</h4>
                  <p className="text-[13px] text-gray-400">Notify orgs approaching the lock date.</p>
                </div>
                <button
                  onClick={() => setDeadlineReminders(!deadlineReminders)}
                  className={`w-[46px] h-[26px] rounded-full transition-colors relative flex items-center ${deadlineReminders ? 'bg-[#086C53]' : 'bg-gray-300'}`}
                >
                  <div className={`w-[20px] h-[20px] bg-white rounded-full transition-transform absolute shadow-sm ${deadlineReminders ? 'translate-x-[22px]' : 'translate-x-[3px]'}`}></div>
                </button>
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button className="px-6 py-2 bg-[#086C53] rounded-lg text-sm font-semibold text-white hover:bg-[#065440] transition-colors shadow-sm">Save</button>
              </div>

            </div>
          </div>
        )}

        {currentTab === 'academic-year' && (
          <div className="space-y-6 max-w-4xl">
            {isOffCampus ? (
              /* Off-Campus Academic Year UI */
              <>
                {/* Current Cycle Banner */}
                <div className="bg-[#054334] rounded-[16px] p-6 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 shadow-lg">
                  <div>
                    <p className="text-green-300 text-[11px] font-bold uppercase tracking-widest mb-1">Current Academic Year</p>
                    <h3 className="text-white text-3xl font-black tracking-tight">
                      {currentOffcampusYear && currentOffcampusYear.is_active
                        ? currentOffcampusYear.year_label
                        : 'No active cycle'}
                    </h3>
                  </div>
                  <button
                    onClick={() => {
                      if (currentOffcampusYear) {
                        setShowOffcampusDownloadPrompt(true);
                        setOffcampusDownloadDone(false);
                        setOffcampusAcknowledged(false);
                      }
                    }}
                    disabled={!currentOffcampusYear}
                    className="flex items-center space-x-2 bg-[#00A358] hover:bg-[#FFFFFF] text-white text-sm font-bold hover:text-black px-5 py-3 rounded-xl transition-colors shadow-md flex-shrink-0"
                  >
                    <span>End Academic Year</span>
                  </button>
                </div>

                {/* New Cycle Setup */}
                <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-[#086C53] px-6 py-4">
                    <h3 className="text-white font-semibold text-[15px]">Set Up Next Academic Year</h3>
                    <p className="text-green-100 text-[11px] mt-0.5">Configure the upcoming cycle dates — takes effect once the current year is archived.</p>
                  </div>
                  <div className="p-8 space-y-6">
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Year Label</h4>
                        <p className="text-[13px] text-gray-500">e.g. 2026-2027</p>
                      </div>
                      <input
                        type="text"
                        value={offcampusYearLabel}
                        onChange={(e) => setOffcampusYearLabel(e.target.value)}
                        placeholder="2026-2027"
                        className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700"
                      />
                    </div>
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Start Date</h4>
                        <p className="text-[13px] text-gray-500">The first day of the new academic year cycle.</p>
                      </div>
                      <input
                        type="date"
                        value={offcampusStartDate}
                        onChange={(e) => setOffcampusStartDate(e.target.value)}
                        className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">End Date</h4>
                        <p className="text-[13px] text-gray-500">The official end date of this academic year.</p>
                      </div>
                      <input
                        type="date"
                        value={offcampusLockDate}
                        onChange={(e) => setOffcampusLockDate(e.target.value)}
                        className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 cursor-pointer"
                      />
                    </div>
                    {offcampusYearError && (
                      <p className="text-red-500 text-sm font-medium">{offcampusYearError}</p>
                    )}
                    <div className="flex justify-end space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setOffcampusYearLabel('');
                          setOffcampusStartDate('');
                          setOffcampusLockDate('');
                          setOffcampusYearError('');
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateOffcampusYear}
                        className="px-6 py-2 bg-[#086C53] rounded-lg text-sm font-semibold text-white hover:bg-[#065440] transition-colors shadow-sm"
                      >
                        Create Academic Year
                      </button>
                    </div>
                  </div>
                </div>

                {/* Cycle History */}
                <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-[#086C53] px-6 py-4">
                    <h3 className="text-white font-semibold text-[15px]">Cycle History</h3>
                    <p className="text-green-100 text-[11px] mt-0.5">All past and current academic year cycles</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {offcampusYearLoading ? (
                      <div className="px-6 py-4 text-sm text-gray-400">Loading...</div>
                    ) : offcampusYears.length === 0 ? (
                      <div className="px-6 py-4 text-sm text-gray-400">
                        No academic years created yet.
                      </div>
                    ) : (
                      offcampusYears.map((ay) => (
                        <div key={ay.offcampus_year_id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center space-x-4">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ay.is_active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <div>
                              <p className="text-sm font-bold text-gray-800">{ay.year_label}</p>
                              <p className="text-xs text-gray-500 font-medium">
                                {ay.start_date ? new Date(ay.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
                                {' — '}
                                End: {ay.lock_date ? new Date(ay.lock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${ay.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                            }`}>{ay.is_active ? 'Current' : 'Archived'}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Off-Campus Download Prompt Modal */}
                {showOffcampusDownloadPrompt && ReactDOM.createPortal(
                  <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                      <div className="bg-red-50 border-b border-red-100 px-6 py-4">
                        <h2 className="text-[18px] font-bold text-red-800">Before you end the academic year</h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed">
                          We recommend downloading all submitted documents first.
                        </p>
                      </div>
                      <div className="px-6 py-5 space-y-4">
                        <button
                          onClick={handleDownloadOffcampusBeforeYearEnd}
                          disabled={downloadingOffcampusDocs}
                          className={`w-full py-3 rounded-xl text-[14px] font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${offcampusDownloadDone
                            ? 'bg-green-100 border-2 border-green-400 text-green-700'
                            : 'bg-[#054334] text-white hover:bg-[#086C53]'
                            }`}
                        >
                          {downloadingOffcampusDocs ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Downloading...
                            </>
                          ) : offcampusDownloadDone ? (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Documents Downloaded
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download All Documents
                            </>
                          )}
                        </button>
                        <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <input
                            type="checkbox"
                            id="offcampus-acknowledge"
                            checked={offcampusAcknowledged}
                            onChange={(e) => setOffcampusAcknowledged(e.target.checked)}
                            className="mt-0.5 w-4 h-4 accent-red-600 cursor-pointer flex-shrink-0"
                          />
                          <label htmlFor="offcampus-acknowledge" className="text-[13px] text-gray-600 leading-relaxed cursor-pointer">
                            I understand that ending the academic year will permanently delete all submitted documents and user accounts.
                          </label>
                        </div>
                      </div>
                      <div className="px-6 pb-5 flex gap-3">
                        <button
                          onClick={() => {
                            setShowOffcampusDownloadPrompt(false);
                            setOffcampusDownloadDone(false);
                            setOffcampusAcknowledged(false);
                          }}
                          className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            setShowOffcampusDownloadPrompt(false);
                            setShowOffcampusEndModal(true);
                            setOffcampusEndConfirmText('');
                          }}
                          disabled={!offcampusAcknowledged}
                          className="flex-1 py-2.5 rounded-xl text-[13px] font-bold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Proceed to End Year
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

                {/* Off-Campus End Year Confirmation Modal */}
                {showOffcampusEndModal && ReactDOM.createPortal(
                  <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-[24px] w-full max-w-[480px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setShowOffcampusEndModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2">
                        <Icons.Close />
                      </button>
                      <div className="p-8">
                        <h2 className="text-[22px] font-bold text-gray-900 text-center mb-2">End Academic Year?</h2>
                        <p className="text-[13px] text-gray-500 text-center mb-2">This will perform the following actions for <span className="font-bold text-gray-800">{currentOffcampusYear ? currentOffcampusYear.year_label : ''}</span>:</p>
                        <ul className="text-[12px] text-gray-600 space-y-2 mb-6 mt-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <li className="flex items-start space-x-2"><span>• All current off-campus records will be <strong>archived</strong>.</span></li>
                          <li className="flex items-start space-x-2"><span>• All current organization representatives will have their dashboard access <strong>revoked</strong>.</span></li>
                          <li className="flex items-start space-x-2"><span>• New representatives will need to <strong>apply for access</strong> to regain access.</span></li>
                        </ul>
                        <div>
                          <label className="block text-sm font-bold text-gray-800 mb-2">Type <span className="text-red-500 font-black">END {currentOffcampusYear ? currentOffcampusYear.year_label : ''}</span> to confirm</label>
                          <input
                            type="text"
                            value={offcampusEndConfirmText}
                            onChange={(e) => setOffcampusEndConfirmText(e.target.value)}
                            placeholder={`END ${currentOffcampusYear ? currentOffcampusYear.year_label : ''}`}
                            className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-300 font-medium"
                          />
                        </div>
                      </div>
                      <div className="px-8 pb-8 flex justify-end space-x-3">
                        <button onClick={() => setShowOffcampusEndModal(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
                        <button
                          disabled={offcampusEndConfirmText !== `END ${currentOffcampusYear ? currentOffcampusYear.year_label : ''}`}
                          onClick={handleEndOffcampusYear}
                          className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors shadow-sm ${offcampusEndConfirmText === `END ${currentOffcampusYear ? currentOffcampusYear.year_label : ''}`
                            ? 'bg-red-500 hover:bg-red-600 cursor-pointer'
                            : 'bg-gray-300 cursor-not-allowed'
                            }`}
                        >
                          End & Archive
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </>
            ) : (
              /* Accreditation Academic Year UI */
              <>
                {/* Current Cycle Banner */}
                <div className="bg-[#054334] rounded-[16px] p-6 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 shadow-lg">
                  <div>
                    <p className="text-green-300 text-[11px] font-bold uppercase tracking-widest mb-1">Current Academic Year</p>
                    <h3 className="text-white text-3xl font-black tracking-tight">{currentAcYear ? currentAcYear.year_label : 'No active cycle'}</h3>

                  </div>
                  <button
                    onClick={() => {
                      if (currentAcYear) {
                        setShowDownloadPromptModal(true);
                        setDownloadAllDone(false);
                        setAcknowledgedDownload(false);
                      }
                    }}
                    disabled={!currentAcYear}
                    className="flex items-center space-x-2 bg-[#00A358] hover:bg-[#FFFFFF] text-white text-sm font-bold hover:text-black px-5 py-3 rounded-xl transition-colors shadow-md flex-shrink-0"
                  >

                    <span>End Academic Year</span>
                  </button>
                </div>

                {/* New Cycle Setup */}
                <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-[#086C53] px-6 py-4">
                    <h3 className="text-white font-semibold text-[15px]">Set Up Next Academic Year</h3>
                    <p className="text-green-100 text-[11px] mt-0.5">Configure the upcoming cycle dates — takes effect once the current year is archived.</p>
                  </div>
                  <div className="p-8 space-y-6">
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Year Label</h4>
                        <p className="text-[13px] text-gray-500">e.g. 2026-2027</p>
                      </div>
                      <input type="text" value={newYearLabel} onChange={(e) => setNewYearLabel(e.target.value)} placeholder="2026-2027" className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700" />
                    </div>
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">Start Date</h4>
                        <p className="text-[13px] text-gray-500">The first day of the new academic year cycle.</p>
                      </div>
                      <input type="date" value={newYearStart} onChange={(e) => setNewYearStart(e.target.value)} className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 cursor-pointer" />
                    </div>
                    <div className="flex justify-between items-start border-b border-gray-100 pb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">End Date</h4>
                        <p className="text-[13px] text-gray-500">The official end date of this academic year.</p>
                      </div>
                      <input type="date" value={newYearEndDate} onChange={(e) => setNewYearEndDate(e.target.value)} className="w-64 bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-[#086C53] font-semibold text-gray-700 cursor-pointer" />
                    </div>
                    {academicYearError && (
                      <p className="text-red-500 text-sm font-medium">{academicYearError}</p>
                    )}
                    <div className="flex justify-end space-x-3 pt-2">
                      <button className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
                      <button onClick={handleCreateAcademicYear} className="px-6 py-2 bg-[#086C53] rounded-lg text-sm font-semibold text-white hover:bg-[#065440] transition-colors shadow-sm">Create Academic Year</button>
                    </div>
                  </div>
                </div>

                {/* Cycle History */}
                <div className="bg-white rounded-[16px] shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-[#086C53] px-6 py-4">
                    <h3 className="text-white font-semibold text-[15px]">Cycle History</h3>
                    <p className="text-green-100 text-[11px] mt-0.5">All past and current academic year cycles</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {academicYearLoading ? (
                      <div className="px-6 py-4 text-sm text-gray-400">Loading...</div>
                    ) : academicYears.length === 0 ? (
                      <div className="px-6 py-4 text-sm text-gray-400">
                        No academic years created yet.
                      </div>
                    ) : (
                      academicYears.map((ay) => (
                        <div key={ay.academic_year_id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center space-x-4">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ay.is_active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <div>
                              <p className="text-sm font-bold text-gray-800">{ay.year_label}</p>
                              <p className="text-xs text-gray-500 font-medium">
                                {new Date(ay.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {' — '}
                                End: {ay.end_date ? new Date(ay.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${ay.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                            }`}>{ay.is_active ? 'Current' : 'Archived'}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Staff Management Tab ─────────────────────────────────────── */}
        {currentTab === 'staff' && (
          <div className="space-y-6 max-w-4xl">

            {/* Header card */}
            <div className="bg-[#054334] rounded-[16px] p-6 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 shadow-lg">
              <div>
                <p className="text-green-300 text-[11px] font-bold uppercase tracking-widest mb-1">System Handover & Succession</p>
                <h3 className="text-white text-2xl font-black tracking-tight">Staff Management</h3>
                <p className="text-green-200 text-[13px] mt-1 font-medium">Manage admin accounts and invite new admin.</p>
              </div>
              <button
                onClick={() => { setShowInviteModal(true); setInviteError(''); setInviteSuccess(''); }}
                className="flex items-center space-x-2 bg-[#00A358] hover:bg-white text-white hover:text-[#054334] text-sm font-bold px-5 py-3 rounded-xl transition-colors shadow-md flex-shrink-0"
              >
                <span>Invite New Admin</span>
              </button>
            </div>

            {/* Info note */}
            <div className="flex items-start space-x-3  rounded-md px-5 py-4">
              <svg className="w-4 h-4 text-yellow-800 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-[12px] text-amber-800 font-medium">
                Deactivated accounts retain all their historical records and review decisions, only their login access is revoked.
              </p>
            </div>

            {/* Staff list */}
            <div className="bg-white rounded-[15px] shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-[#086C53] px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-[18px]">System Administrators</h3>
                  <p className="text-green-100 text-[11px] mt-0.5">{staffList.length} admin account{staffList.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {staffLoading ? (
                <div className="px-6 py-10 text-center text-sm text-gray-400">Loading staff...</div>
              ) : staffList.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-400">No admin accounts found.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {staffList.map((member) => {
                    const initials = (member.full_name || 'A').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                    const isCurrentUser = (() => {
                      try {
                        const u = JSON.parse(localStorage.getItem('user'));
                        return u?.user_id === member.user_id;
                      } catch { return false; }
                    })();
                    const roleLabel = member.admin_type === 'offcampus' ? 'Off-Campus Admin' : 'Accreditation Admin';
                    const roleColor = member.admin_type === 'offcampus'
                      ? 'bg-white text-[#054334] border border-[#054334] rounded-md'
                      : 'bg-white text-[#00A358] border border-[#00A358] rounded-md';

                    return (
                      <div key={member.user_id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full bg-[#086C53] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {initials}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                              <p className="text-sm font-bold text-gray-800">{member.full_name}</p>
                              {isCurrentUser && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#086C53]/10 text-[#086C53] border border-[#086C53]/20">You</span>
                              )}
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${roleColor}`}>{roleLabel}</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${member.status === 'active' ? 'bg-green-100 text-green-700' : member.status === 'inactive' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'}`}>
                                {member.status === 'active' ? 'Active' : member.status === 'inactive' ? 'Inactive' : 'Pending Setup'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 font-medium mt-0.5">{member.slsu_email}</p>
                          </div>
                        </div>

                        {!isCurrentUser && (
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {/* Deactivate / Reactivate button */}
                            <button
                              onClick={() => handleToggleStaffStatus(member)}
                              disabled={staffActionLoading === member.user_id || (deletingStaffLoading && deletingStaff?.user_id === member.user_id)}
                              className={`px-4 py-2 text-xs font-bold rounded-xl transition-colors shadow-sm disabled:opacity-50 text-white cursor-pointer ${member.status === 'active'
                                ? 'bg-[#DD3D4A] border border-[#DD3D4A] hover:bg-white hover:text-[#DD3D4A] hover:border-[#DD3D4A]'
                                : 'bg-[#00A358] border border-[#00A358] hover:bg-white hover:text-[#00A358] hover:border-[#00A358]'
                                }`}
                            >
                              {staffActionLoading === member.user_id
                                ? (member.status === 'active' ? 'Deactivating...' : 'Reactivating...')
                                : (member.status === 'active' ? 'Deactivate' : 'Reactivate')}
                            </button>

                            {/* Delete button (icon only) */}
                            <button
                              onClick={() => setDeletingStaff(member)}
                              disabled={staffActionLoading === member.user_id || (deletingStaffLoading && deletingStaff?.user_id === member.user_id)}
                              title="Delete admin account"
                              className="p-2 text-red-600 bg-red-50 hover:bg-white hover:text-red-700 border border-red-200 rounded-xl transition-all duration-150 shadow-sm disabled:opacity-50 flex items-center justify-center cursor-pointer"
                            >
                              <Icons.Trash className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        )}

      </div>

      {/* ── Invite New Admin Modal ────────────────────────────────────────────── */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] w-full max-w-[480px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowInviteModal(false); setInviteError(''); setInviteSuccess(''); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full p-2">
              <Icons.Close />
            </button>

            <div className="px-8 pt-8 pb-4">

              <h2 className="text-[22px] font-bold text-gray-900 text-center mb-10">Invite New Admin</h2>
              <p className="text-[13px] text-gray-500 mt-1">The invited person will receive an email with a one-time code to set their own password.</p>
            </div>

            <div className="px-8 pb-2 space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-gray-700 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  placeholder="e.g. Maria Santos"
                  className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-700 mb-1.5">Institutional Email</label>
                <input
                  type="email"
                  value={inviteForm.slsu_email}
                  onChange={(e) => setInviteForm({ ...inviteForm, slsu_email: e.target.value })}
                  placeholder="staff@slsu.edu.ph"
                  className="w-full bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-700 mb-1.5">Role</label>
                <div className="relative">
                  <select
                    value={inviteForm.admin_type}
                    onChange={(e) => setInviteForm({ ...inviteForm, admin_type: e.target.value })}
                    className="w-full appearance-none bg-[#f8fafc] border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition cursor-pointer"
                  >
                    <option value="accreditation">Accreditation / Re-accreditation Admin</option>
                    <option value="offcampus">Off-Campus Activity Admin (Super)</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <Icons.ChevronDown />
                  </div>
                </div>
              </div>

              {inviteError && (
                <p className="text-red-500 text-[13px] font-medium bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl">{inviteError}</p>
              )}
              {inviteSuccess && (
                <p className="text-green-700 text-[13px] font-medium bg-green-50 border border-green-100 px-4 py-2.5 rounded-xl">{inviteSuccess}</p>
              )}
            </div>

            <div className="px-8 pt-4 pb-8 flex gap-3">
              <button
                onClick={() => { setShowInviteModal(false); setInviteError(''); setInviteSuccess(''); }}
                className="flex-1 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviteLoading || !!inviteSuccess}
                className="flex-1 py-3 bg-[#086C53] rounded-xl text-sm font-bold text-white hover:bg-[#065440] transition-colors shadow-sm disabled:opacity-60"
              >
                {inviteLoading ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {viewingDoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-[#e5e7eb] rounded-[24px] w-full max-w-4xl h-[80vh] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-1.5 bg-gray-100 rounded text-gray-500"><Icons.Form1 /></div>
                <h3 className="font-bold text-gray-800 text-lg">{viewingDoc.document_name}</h3>
              </div>
              <button onClick={() => setViewingDoc(null)} className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2">
                <Icons.Close />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4 custom-scrollbar relative">
              {viewingDoc.template_file_path ? (
                <div className="w-full h-full">
                  <FileViewer
                    fileUrl={viewingDoc.template_file_path}
                    fileName={viewingDoc.document_name}
                    filePath={viewingDoc.template_file_path}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center p-8">
                  <div className="text-gray-300 mb-4 scale-150"><Icons.Form1 /></div>
                  <p className="font-medium text-lg">No template file uploaded for {viewingDoc.document_name}</p>
                  <p className="text-sm mt-2 max-w-sm">Delete this requirement and re-add it to upload a template file.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add New Requirement Modal */}
      {addingReqType && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] w-full max-w-[500px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-8 pt-8 pb-4">
              <div>
                <h2 className="text-[22px] font-bold text-gray-900">Add New Requirement</h2>
                <p className="text-[13px] text-gray-500 mt-0.5">{addingReqType === 'accreditation' ? 'Accreditation' : addingReqType === 'reaccreditation' ? 'Re-accreditation' : 'Off-Campus Activity'} requirement</p>
              </div>
              <button onClick={() => { setAddingReqType(null); setNewReqFile(null); setIsDragOver(false); }} className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full p-2">
                <Icons.Close />
              </button>
            </div>

            {/* Drop Zone */}
            <div className="px-8 pb-2">
              <div
                className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${isDragOver ? 'border-[#086C53] bg-[#086C53]/5' : newReqFile ? 'border-[#086C53] bg-[#086C53]/5' : 'border-gray-300 hover:border-[#086C53] hover:bg-[#086C53]/5'
                  }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const file = e.dataTransfer.files[0]; if (file) setNewReqFile(file); }}
                onClick={() => document.getElementById('req-file-input').click()}
              >
                {/* Upload cloud icon */}
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${newReqFile ? 'bg-[#086C53]/15' : 'bg-[#086C53]/10'}`}>
                  <svg className="w-7 h-7 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                {newReqFile ? (
                  <div className="text-center">
                    <p className="text-[14px] font-bold text-[#086C53]">{newReqFile.name}</p>
                    <p className="text-[12px] text-gray-400 mt-1">{(newReqFile.size / 1024).toFixed(1)} KB — Click to change file</p>
                  </div>
                ) : (
                  <p className="text-[14px] text-gray-600">
                    Drag or drop files, or{' '}
                    <span className="text-[#086C53] font-semibold underline underline-offset-2">Browse</span>
                  </p>
                )}
              </div>
              <input
                id="req-file-input"
                type="file"
                className="hidden"
                onChange={(e) => { if (e.target.files[0]) setNewReqFile(e.target.files[0]); }}
              />
              <p className="text-[11px] text-gray-400 mt-3 text-center">
                The filename (without extension) becomes the requirement name. The file itself is not stored.
              </p>
            </div>

            {/* Footer */}
            <div className="px-8 pt-4 pb-8">
              <button
                onClick={handleUploadNewRequirement}
                disabled={!newReqFile}
                className={`w-full py-3 rounded-xl text-[15px] font-bold transition-all duration-200 shadow-sm ${newReqFile ? 'bg-[#086C53] text-white hover:bg-[#065440] cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                Add Requirement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Requirement Modal */}
      {deletingReq && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] w-full max-w-[400px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setDeletingReq(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2">
              <Icons.Close />
            </button>
            <div className="p-8 pb-6 text-center pt-10">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <h2 className="text-[20px] font-bold text-gray-900 mb-2">Delete Requirement?</h2>
              <p className="text-[14px] text-gray-500 px-4">Are you sure you want to delete <span className="font-semibold text-gray-700">"{deletingReq.name}"</span>? This will also remove it from the checklist of organizations that have not submitted it yet.</p>
            </div>
            <div className="p-6 pt-0 flex justify-center space-x-3">
              <button onClick={() => setDeletingReq(null)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleDeleteRequirement} className="px-6 py-2.5 bg-red-500 rounded-lg text-sm font-semibold text-white hover:bg-red-600 transition-colors shadow-sm">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Admin Account Confirmation Modal */}
      {deletingStaff && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[120] p-4 backdrop-blur-sm" onClick={() => !deletingStaffLoading && setDeletingStaff(null)}>
          <div className="bg-white rounded-[24px] w-full max-w-[420px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => !deletingStaffLoading && setDeletingStaff(null)}
              disabled={deletingStaffLoading}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2 disabled:opacity-50"
            >
              <Icons.Close />
            </button>
            <div className="p-8 pb-5 text-center pt-10">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h2 className="text-[20px] font-bold text-gray-900 mb-2">Delete Admin?</h2>
              <p className="text-[14px] text-gray-500 px-2 leading-relaxed">
                Are you sure you want to permanently delete the admin account for{' '}
                <span className="font-semibold text-gray-800">{deletingStaff.full_name}</span>{' '}
                <span className="text-gray-400 font-mono text-[12px]">({deletingStaff.slsu_email})</span>?
              </p>
              <div className="mt-4 bg-red-50/70 border border-red-100 rounded-xl p-3.5 text-left">
                <p className="text-[12px] text-red-700 leading-normal text-center">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="p-6 pt-2 flex justify-center space-x-3">
              <button
                onClick={() => setDeletingStaff(null)}
                disabled={deletingStaffLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStaff}
                disabled={deletingStaffLoading}
                className="px-5 py-2.5 bg-red-600 rounded-lg text-sm font-semibold text-white hover:bg-red-700 transition-colors shadow-sm flex items-center justify-center space-x-1.5 disabled:opacity-50 cursor-pointer"
              >
                {deletingStaffLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Prompt Modal — shown before End Academic Year confirmation */}
      {showDownloadPromptModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-4">
              <h2 className="text-[18px] font-bold text-red-800">Before you end the academic year</h2>
              <p className="text-[13px] text-gray-600 leading-relaxed ">
                We recommend downloading all submitted documents first.
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">

              {/* Download Button */}
              <button
                onClick={handleDownloadAllBeforeYearEnd}
                disabled={downloadingAllDocs}
                className={`w-full py-3 rounded-xl text-[14px] font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${downloadAllDone
                  ? 'bg-green-100 border-2 border-green-400 text-green-700'
                  : 'bg-[#054334] text-white hover:bg-[#086C53]'
                  }`}
              >
                {downloadingAllDocs ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Downloading...
                  </>
                ) : downloadAllDone ? (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Documents Downloaded
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download All Documents
                  </>
                )}
              </button>

              {/* Acknowledge Checkbox */}
              <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-3 border border-gray-200">
                <input
                  type="checkbox"
                  id="acknowledge-download"
                  checked={acknowledgedDownload}
                  onChange={(e) => setAcknowledgedDownload(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-red-600 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="acknowledge-download" className="text-[13px] text-gray-600 leading-relaxed cursor-pointer">
                  I understand that ending the academic year will permanently delete all submitted documents and user accounts.
                </label>
              </div>
            </div>

            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={() => {
                  setShowDownloadPromptModal(false);
                  setDownloadAllDone(false);
                  setAcknowledgedDownload(false);
                }}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDownloadPromptModal(false);
                  setShowEndCycleModal(true);
                  setEndCycleConfirmText('');
                }}
                disabled={!acknowledgedDownload}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-bold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Proceed to End Year
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* End Academic Year Confirmation Modal */}
      {showEndCycleModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] w-full max-w-[480px] shadow-2xl overflow-hidden flex flex-col relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowEndCycleModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2">
              <Icons.Close />
            </button>
            <div className="p-8">

              <h2 className="text-[22px] font-bold text-gray-900 text-center mb-2">End Academic Year?</h2>
              <p className="text-[13px] text-gray-500 text-center mb-2">This will perform the following actions for <span className="font-bold text-gray-800">{currentAcYear ? currentAcYear.year_label : ''}</span>:</p>
              <ul className="text-[12px] text-gray-600 space-y-2 mb-6 mt-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                <li className="flex items-start space-x-2"><span>• All current accreditation records will be <strong>archived</strong>.</span></li>
                <li className="flex items-start space-x-2"><span>• All current organization presidents will have their dashboard access <strong>revoked</strong>.</span></li>
                <li className="flex items-start space-x-2"><span>• New presidents will need to <strong>apply for renewal</strong> to regain access.</span></li>
              </ul>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">Type <span className="text-red-500 font-black">END {currentAcYear ? currentAcYear.year_label : ''}</span> to confirm</label>
                <input
                  type="text"
                  value={endCycleConfirmText}
                  onChange={(e) => setEndCycleConfirmText(e.target.value)}
                  placeholder={`END ${currentAcYear ? currentAcYear.year_label : ''}`}
                  className="w-full border border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-300 font-medium"
                />
              </div>
            </div>
            <div className="px-8 pb-8 flex justify-end space-x-3">
              <button onClick={() => setShowEndCycleModal(false)} className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                disabled={endCycleConfirmText !== `END ${currentAcYear ? currentAcYear.year_label : ''}`}
                onClick={handleEndAcademicYear}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors shadow-sm ${endCycleConfirmText === `END ${currentAcYear ? currentAcYear.year_label : ''}`
                  ? 'bg-red-500 hover:bg-red-600 cursor-pointer'
                  : 'bg-gray-300 cursor-not-allowed'
                  }`}
              >
                End & Archive
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default Settings;